
/**
 * MobileFilterModal.tsx
 *
 * This component renders a modal sidebar for filtering solutions on mobile devices.
 * It overlays the main content, prevents accidental closure via Escape or backdrop click,
 * and provides a consistent, accessible filter experience. Integrates with FilterSidebar.
 *
 * Usage: Used in MainContent to provide mobile-friendly filter controls.
 *
 * @author Ellucian Marketplace Team
 * @date 2025-09-10
 */

import React from 'react';
import { Icon } from '@ellucian/ds-icons/lib';
import FilterSidebar from './FilterSidebar';

/**
 * Props for MobileFilterModal
 * @property isOpen - Whether the modal is open
 * @property onClose - Callback to close the modal
 * @property createdByOptions - List of author options for filtering
 * @property tagList - List of tags for filtering
 * @property typeList - List of solution types for filtering
 * @property integratedSolutions - List of integrated solutions for filtering
 * @property selectedAuthors - Currently selected authors
 * @property setSelectedAuthors - Setter for selected authors
 * @property selectedTypes - Currently selected types
 * @property setSelectedTypes - Setter for selected types
 * @property selectedIntegratedSolutions - Currently selected integrated solutions
 * @property setSelectedIntegratedSolutions - Setter for selected integrated solutions
 * @property selectedCategories - (Optional) Currently selected categories
 * @property setSelectedCategories - (Optional) Setter for selected categories
 * @property isLoggedIn - Whether the user is currently logged in
 * @property showOnlyEntitledSolutions - Whether to show only entitled solutions
 * @property setShowOnlyEntitledSolutions - Callback to update entitled filter
 */
interface MobileFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  createdByOptions: string[];
  tagList: string[];
  typeList: string[];
  integratedSolutions: string[];
  selectedAuthors: string[];
  setSelectedAuthors: (authors: string[]) => void;
  selectedTypes: string[];
  setSelectedTypes: (types: string[]) => void;
  selectedIntegratedSolutions: string[];
  setSelectedIntegratedSolutions: (solutions: string[]) => void;
  selectedCategories?: string[];
  setSelectedCategories?: (categories: string[]) => void;
  isLoggedIn?: boolean;
  showOnlyEntitledSolutions?: boolean;
  setShowOnlyEntitledSolutions?: (show: boolean) => void;
  showCoreEligibleOnly?: boolean;
  setShowCoreEligibleOnly?: (value: boolean) => void;
  deliveryModelOptions?: { label: string; value: string }[];
  selectedDeliveryModels?: string[];
  setSelectedDeliveryModels?: (models: string[]) => void;
  showAdditionalPurchaseOnly?: boolean;
  setShowAdditionalPurchaseOnly?: (value: boolean) => void;
}


/**
 * MobileFilterModal component
 * Renders a modal sidebar for mobile filter controls. Prevents accidental closure via Escape or backdrop click.
 * Integrates with FilterSidebar for filter options. Used in mobile viewports.
 */
const MobileFilterModal: React.FC<MobileFilterModalProps> = ({
  isOpen,
  onClose,
  createdByOptions,
  tagList,
  typeList,
  integratedSolutions,
  selectedAuthors,
  setSelectedAuthors,
  selectedTypes,
  setSelectedTypes,
  selectedIntegratedSolutions,
  setSelectedIntegratedSolutions,
  selectedCategories = [],
  setSelectedCategories = () => {},
  isLoggedIn = false,
  showOnlyEntitledSolutions = false,
  setShowOnlyEntitledSolutions = () => {},
  showCoreEligibleOnly = false,
  setShowCoreEligibleOnly = () => {},
  deliveryModelOptions = [],
  selectedDeliveryModels = [],
  setSelectedDeliveryModels = () => {},
  showAdditionalPurchaseOnly = false,
  setShowAdditionalPurchaseOnly = () => {},
}) => {
  // Effect: Prevent modal from closing on Escape key (for mobile UX consistency)
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen]);

  // Effect: Lock body scroll while modal is open to prevent background scroll
  React.useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPaddingBottom = document.body.style.paddingBottom;
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // ensure we account for safe-area inset on some devices
      document.body.style.paddingBottom = 'env(safe-area-inset-bottom)';
    }
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingBottom = previousPaddingBottom;
    };
  }, [isOpen]);

  // If modal is not open, render nothing
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop: visually dims background, does NOT close modal on click */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
        }}
        // Intentionally no onClick to prevent accidental closure
      />

      {/* Modal container: slides in from right, contains header, content, and footer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '85%',
          maxWidth: '400px',
          height: '100vh',
          backgroundColor: '#fff',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
          zIndex: 1001,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header: Title and close button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontWeight: 700,
              fontSize: '18px',
              color: '#151618',
            }}
          >
            Filters
          </h2>
          {/* Close button: triggers onClose callback */}
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Close filter modal"
          >
            <Icon name="close" style={{ width: '20px', height: '20px', color: '#6B7280' }} />
          </button>
        </div>

        {/* Filter Content: scrollable area with filter controls */}
        <div
          style={{
            flex: 1,
            overflow: 'auto', // Allow scrolling in mobile modal if needed
            padding: '20px 20px 64px 20px', // Reduced bottom padding so content clears footer but gap is smaller
          }}
          className="mobile-filter-modal-scrollable"
        >
          <style>{`
            .mobile-filter-modal-scrollable::-webkit-scrollbar {
              width: 12px;
              -webkit-appearance: none;
            }
            .mobile-filter-modal-scrollable::-webkit-scrollbar-track {
              background: #f3f4f6;
              border-radius: 6px;
              border: 2px solid #e5e7eb;
            }
            .mobile-filter-modal-scrollable::-webkit-scrollbar-thumb {
              background: #6b7280;
              border-radius: 6px;
              border: 1px solid #4b5563;
              min-height: 40px;
            }
            .mobile-filter-modal-scrollable::-webkit-scrollbar-thumb:hover {
              background: #4b5563;
            }
            .mobile-filter-modal-scrollable {
              scrollbar-width: auto;
              scrollbar-color: #6b7280 #f3f4f6;
              -webkit-overflow-scrolling: touch;
            }
          `}</style>
          {/* FilterSidebar: actual filter controls */}
          <FilterSidebar
            createdByOptions={createdByOptions}
            tagList={tagList}
            typeList={typeList}
            integratedSolutions={integratedSolutions}
            selectedAuthors={selectedAuthors}
            setSelectedAuthors={setSelectedAuthors}
            selectedTypes={selectedTypes}
            setSelectedTypes={setSelectedTypes}
            selectedIntegratedSolutions={selectedIntegratedSolutions}
            setSelectedIntegratedSolutions={setSelectedIntegratedSolutions}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            isLoggedIn={isLoggedIn}
            showOnlyEntitledSolutions={showOnlyEntitledSolutions}
            setShowOnlyEntitledSolutions={setShowOnlyEntitledSolutions}
            showCoreEligibleOnly={showCoreEligibleOnly}
            setShowCoreEligibleOnly={setShowCoreEligibleOnly}
            deliveryModelOptions={deliveryModelOptions}
            selectedDeliveryModels={selectedDeliveryModels}
            setSelectedDeliveryModels={setSelectedDeliveryModels}
            showAdditionalPurchaseOnly={showAdditionalPurchaseOnly}
            setShowAdditionalPurchaseOnly={setShowAdditionalPurchaseOnly}
          />
        </div>

        {/* Footer: Apply button to close modal and apply filters. Sticky so always visible */}
        <div
          style={{
            position: 'sticky',
            bottom: 14, /* give some margin from bottom (+1px requested) */
            padding: '16px 20px',
            borderTop: '1px solid #E5E7EB',
            backgroundColor: '#fff',
            flexShrink: 0,
            /* ensure the footer sits above device safe area and includes extra margin */
            paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
          }}
        >
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '12px',
              background: '#7100EB',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontWeight: 600,
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileFilterModal;